import { Component, computed, DestroyRef, inject, input, linkedSignal, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideSearch } from '@ng-icons/lucide';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmAutocompleteImports, provideHlmAutocompleteConfig } from '@spartan-ng/helm/autocomplete';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { Référentiel } from '../../../../../../administration/gestion-référentiels/models/référentiel.model';
import { ExpansionPanel } from '../../../../../../shared/components/expansion-panel/expansion-panel.component';
import { IndiceRésultats } from '../../../../processus-indice-resultats/models/indice-résultats';
import { IndicateurPerformance } from '../../../models/indicateur-performance';
import { IndicateurPerformanceStore } from '../../../store/indicateur-performance.store';

type NouvelIndicateur = Omit<IndicateurPerformance, 'id' | 'idsIndicesResultats'>;

@Component({
  selector: 'app-form-item-indicateur',
  imports: [
    ReactiveFormsModule,
    HlmInputImports,
    HlmFieldImports,
    HlmInputGroupImports,
    NgIcon,
    HlmSelectImports,
    BrnSelectImports,
    ExpansionPanel,
    LabelRequiredDirective,
    HlmAutocompleteImports,
  ],
  templateUrl: './form-item-indicateur.component.html',
  providers: [
    provideHlmAutocompleteConfig({
      transformValueToSearch: (option: IndicateurPerformance | string) =>
        `${typeof option === 'string' ? option : option.nom}`,
    }),
    provideIcons({ lucideSearch }),
  ],
})
export class FormItemIndicateurComponent implements OnInit {
  readonly #indicateurStore = inject(IndicateurPerformanceStore);
  readonly #destroy = inject(DestroyRef);

  readonly idx = input.required<number>();
  readonly form = input.required<FormGroup>();
  readonly referentiel = input.required<Référentiel>();
  readonly indiceResultats = input.required<IndiceRésultats>();
  readonly indicesGroups = input.required<{ nom: string; items: IndiceRésultats[] }[]>();
  readonly indicesFiltres = linkedSignal<{ nom: string; items: IndiceRésultats[] }[]>(() => this.indicesGroups());
  public readonly indicateursFiltres = computed(() =>
    this.#indicateurStore
      .entities()
      .filter(
        (indicateur) =>
          indicateur.nom.toLowerCase().includes(this.search().toLowerCase()) ||
          `${indicateur.nom}`.toLowerCase().includes(this.search().toLowerCase()),
      ),
  );
  search = signal('');
  nouveauxIndicateurs = signal<NouvelIndicateur[]>([]);
  tousLesIndicateurs = computed(() => [...this.indicateursFiltres(), ...this.nouveauxIndicateurs()]);

  ngOnInit(): void {
    this.nomIndice.valueChanges
      .pipe(debounceTime(10), takeUntilDestroyed(this.#destroy), distinctUntilChanged())
      .subscribe(() => {
        this.indicesFiltres.set(
          this.indicesGroups().filter(
            (group) =>
              group.items.filter((x) => x.nom.toLowerCase().includes(this.nomIndice.value.toLowerCase())).length > 0,
          ),
        );
      });

    this.indicateur.valueChanges
      .pipe(debounceTime(10), takeUntilDestroyed(this.#destroy), distinctUntilChanged())
      .subscribe(() => {
        const indicateur = this.indicateur.value;
        if (indicateur && 'idsIndicesResultats' in indicateur) {
          this.indices.setValue([this.indiceResultats().id, ...indicateur.idsIndicesResultats]);
        }
      });
  }

  /** Si le texte saisi ne correspond à aucune option, il est envoyé tel quel au formControl sous forme de nouvel indicateur. */
  onSearchChange(texte: string) {
    const nom = texte.trim();
    if (!nom || this.indicateur.value?.nom === nom) {
      return;
    }

    const existant = this.#indicateurStore.entities().find((x) => x.nom.toLowerCase() === nom.toLowerCase());
    this.indicateur.setValue(existant ?? { nom }, { emitModelToViewChange: false });
  }

  isFormInvalid() {
    return (!this.indicateur.value || this.search() === '') && this.form().touched;
  }

  get nomIndice() {
    return this.form().get('nomIndice') as FormControl<string>;
  }

  get indicateur() {
    return this.form().get('indicateur') as FormControl<IndicateurPerformance | NouvelIndicateur>;
  }

  get indices() {
    return this.form().get('indices') as FormControl<string[]>;
  }
}
